import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "../ui/LoadingButton";
import { toast } from "react-toastify";
import { useIntl } from "../../contexts/IntlContext";

// Fixed on purpose (not theme colors): the PNG is printed, so it must not change with the
// admin's light/dark theme. Dark modules on a light background (the lime is light, contrast
// ~17:1 with the modules) keep the code readable by scanners; never invert them. Swap
// QR_LIGHT_COLOR for "#ffffff" if a scanner or printer struggles with the lime.
const QR_DARK_COLOR = "#07090c";
const QR_LIGHT_COLOR = "#C3FF4D";
const QR_EXPORT_SIZE_PX = 1024;
const QR_QUIET_ZONE_MODULES = 4;

type Props = {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
};

function getFileName(url: string): string {
  const lastSegment = url.split(/[?#]/)[0].split("/").filter(Boolean).pop() ?? "";
  const safeName = lastSegment.replace(/[^a-zA-Z0-9._-]/g, "-");
  return `${safeName || "qrcode"}.png`;
}

export default function QrCodeDialog({ open, onClose, url, title }: Props) {
  const intl = useIntl();
  // Keyed by url so a previous link's image is never shown (or downloaded) for a new url.
  const [generated, setGenerated] = useState<{ url: string; dataUrl: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const dataUrl = generated?.url === url ? generated.dataUrl : null;

  useEffect(() => {
    if (!open) return;
    if (!url) {
      setFailed(true);
      return;
    }

    let cancelled = false;
    setFailed(false);

    // Loaded on demand so the QR library stays out of the main bundle.
    import("qrcode")
      .then((QRCode) =>
        QRCode.toDataURL(url, {
          errorCorrectionLevel: "H",
          margin: QR_QUIET_ZONE_MODULES,
          width: QR_EXPORT_SIZE_PX,
          color: { dark: QR_DARK_COLOR, light: QR_LIGHT_COLOR },
        })
      )
      .then((result) => {
        if (!cancelled) setGenerated({ url, dataUrl: result });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, url]);

  async function handleDownload() {
    if (!dataUrl) return;
    try {
      // Blob URLs download reliably where `download` on a data: URL is ignored (e.g. iOS Safari).
      const blob = await (await fetch(dataUrl)).blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = getFileName(url);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "qrCodeDialog.downloadError" }));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title ?? intl.formatMessage({ id: "qrCodeDialog.title" })}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        {dataUrl ? (
          <Box
            component="img"
            src={dataUrl}
            alt={intl.formatMessage({ id: "qrCodeDialog.alt" })}
            sx={{ width: "100%", maxWidth: 280, borderRadius: 1 }}
          />
        ) : failed ? (
          <Typography color="error">
            {intl.formatMessage({ id: "qrCodeDialog.error" })}
          </Typography>
        ) : (
          <CircularProgress />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          {url}
        </Typography>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          fullWidth={false}
          variant="text"
          onClick={onClose}
        >
          {intl.formatMessage({ id: "common.close" })}
        </LoadingButton>
        <LoadingButton
          fullWidth={false}
          startIcon={<DownloadIcon />}
          disabled={!dataUrl}
          onClick={handleDownload}
        >
          {intl.formatMessage({ id: "qrCodeDialog.download" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
